import BlockContent from '@sanity/block-content-to-react';
import React from 'react';
import { portableTextSerializer } from '@/utils/helpers';
import styles from './styles.module.css';

const makeSerializers = (narrow?: boolean) => {
  const narrowClass = narrow ? 'max-w-3xl' : '';

  const blockSerializer = (props: { node: { style: string }; children: React.ReactNode }) => {
    switch (props.node.style) {
      case 'h1':
        return <h1 className={narrowClass}>{props.children}</h1>;
      case 'h2':
        return <h2 className={narrowClass}>{props.children}</h2>;
      case 'h3':
        return <h3 className={narrowClass}>{props.children}</h3>;
      case 'h4':
        return <h4 className={narrowClass}>{props.children}</h4>;
      case 'h5':
        return <h5 className={narrowClass}>{props.children}</h5>;
      case 'blockquote':
        return (
          <blockquote className={narrowClass}>
            <p>{props.children}</p>
          </blockquote>
        );
      default:
        return <p className={narrowClass}>{props.children}</p>;
    }
  };

  return {
    ...portableTextSerializer,
    types: {
      ...portableTextSerializer.types,
      block: blockSerializer,
    },
    list: ({ type, children }: { type: string; children: React.ReactNode }) => {
      const Tag = type === 'number' ? 'ol' : 'ul';
      return <Tag className={narrowClass}>{children}</Tag>;
    },
    marks: {
      ...portableTextSerializer.marks,
      italic: ({ children }: { children: React.ReactNode }) => <em>{children}</em>,
    },
  };
};

interface BodyProps {
  heading: string;
  alignment?: string;
  content?: Record<string, unknown>[];
  bgColor?: Record<string, unknown>;
  textColor?: Record<string, unknown>;
  narrow?: boolean;
}

const Body: React.FC<BodyProps> = ({ heading, alignment = 'left', content, bgColor, textColor, narrow }) => {
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    if (bgColor?.hex) {
      wrapperRef.current.style.setProperty('--bgColor', `${bgColor.hex}`);
    }
    if (textColor?.hex) {
      wrapperRef.current.style.setProperty('--fontColor', `${textColor.hex}`);
    }
  }, [wrapperRef, bgColor, textColor]);
  const isImage = content.map((item) => Object.keys(item).includes('image')).find((item) => item === true);
  return (
    <section ref={wrapperRef} className={styles.wrapper}>
      <div className={narrow ? `${styles.body} ${styles.bodyNarrow}` : styles.body}>
        <div className={styles[alignment]}>
          <h2 className={narrow ? `${styles.heading} max-w-3xl` : styles.heading}>{heading}</h2>
          {content && (
            <div
              className={isImage && !narrow ? `${styles.textContent} ${styles.extraSpacing}` : `${styles.textContent}`}
            >
              <BlockContent blocks={content} serializers={makeSerializers(narrow)} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Body;
